import { Outlet, createRootRouteWithContext } from '@tanstack/react-router'
import { Toaster } from 'sonner'
import * as Sentry from '@sentry/react'
import type { QueryClient } from '@tanstack/react-query'
import type { Store } from '@tanstack/react-store'
import type { AuthState } from '../stores/auth.store'

interface MyRouterContext {
  queryClient: QueryClient
  auth: Store<AuthState>
}

function ErrorFallback({
  error: _error,
  eventId,
}: {
  error: unknown
  eventId?: string
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 bg-background">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-destructive">
            Something went wrong
          </h1>
          <p className="text-muted-foreground">
            We've been notified and are working on a fix. Please try refreshing
            the page.
          </p>
        </div>

        {eventId && (
          <div className="text-xs text-muted-foreground bg-muted p-3 rounded-md">
            <p className="font-medium mb-1">Error ID:</p>
            <p className="font-mono">{eventId}</p>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Reload Page
          </button>
          {eventId && (
            <button
              onClick={() =>
                Sentry.showReportDialog({ eventId: eventId || undefined })
              }
              className="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
            >
              Report Issue
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function RootComponent() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <Outlet />
      <Toaster richColors position="bottom-center" />
    </Sentry.ErrorBoundary>
  )
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
})
